import { NgClass } from '@angular/common'
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  linkedSignal,
} from '@angular/core'
import {
  takeUntilDestroyed,
  toObservable,
  toSignal,
} from '@angular/core/rxjs-interop'

import { TranslatePipe } from '@ngx-translate/core'
import { startWith, switchMap } from 'rxjs'

import { AdvertisementService } from '../../api/advertisement-service/advertisement.service'
import type {
  AdvertisementImageDto,
  AdvertisementPlacement,
} from '../../api/responses'
import { firstText } from '../../shared/seo/seo.util'

/**
 * Canvas the two artworks are designed on. The banner never grows past this, so
 * a slot wider than the artwork lets the grey band carry on behind it instead
 * of scaling the whole thing up.
 */
const DESIGN = {
  mobile: { width: 370, height: 308 },
  desktop: { width: 1220, height: 420 },
}

/**
 * Margin the artwork leaves above and below the grey band, in design pixels -
 * the room the magazine cover sticks out into. The artwork is transparent there
 * _and_ across the band itself, so the grey comes from this component.
 */
const BAND_MARGIN = 40

/** Where the grey band sits, as a share of the artwork box's height. */
interface BandGeometry {
  top: number
  height: number
}

/** Whether an upload can be measured at all. */
function hasSize(
  image: AdvertisementImageDto | undefined
): image is AdvertisementImageDto & { width: number; height: number } {
  return Boolean(image?.width && image.height)
}

/**
 * The band for an artwork of this size, or the band the design prescribes when
 * the upload is not something the margin can be read off.
 *
 * Reading it off the file keeps the band lined up with artwork exported at any
 * scale: the margin is a fixed 40 design pixels, so in the file it measures
 * `40
 *
 * - (width / designWidth)`. An upload that is nothing like the design - a square
 *   image, a sliver - would put the band somewhere absurd, so those fall back
 *   to the design instead, and the template caps the artwork so the slot cannot
 *   grow past its designed height either way.
 */
function bandFor(
  image: AdvertisementImageDto | undefined,
  design: { width: number; height: number }
): BandGeometry {
  const fallback = {
    top: (BAND_MARGIN / design.height) * 100,
    height: ((design.height - 2 * BAND_MARGIN) / design.height) * 100,
  }
  if (!hasSize(image)) return fallback

  const top =
    ((BAND_MARGIN * image.width) / (design.width * image.height)) * 100

  // Only within reach of where the design puts it. Artwork exported a little
  // taller or shorter than the canvas still lines up exactly, while a square
  // image or a sliver - where 40 design pixels mean nothing - gets the design
  // band with the artwork scaled to fit on top of it.
  const withinReachOfDesign = top >= fallback.top / 2 && top <= fallback.top * 2

  return withinReachOfDesign ? { top, height: 100 - 2 * top } : fallback
}

/**
 * A single banner slot. The page it sits on only says _where_ it is and _which_
 * document it belongs to; picking an advertisement (or none) is entirely the
 * API's call - see AdvertisementService.
 *
 * The artwork is capped at its design size and centred, on top of a grey band
 * that runs the full width of the slot. The banner is meant to reach the edges
 * of the page, which the page it sits in arranges by passing negative
 * horizontal margins that cancel its own padding (`-mx-6 md:-mx-16` and
 * friends). Those are safe on the host because horizontal margins on an empty
 * element show up as nothing at all - and so is the vertical spacing, which
 * lives on the rendered anchor, so a slot without a banner takes up no room.
 */
@Component({
  selector: 'dhp-advertisement',
  templateUrl: './advertisement.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'block' },
  imports: [NgClass, TranslatePipe],
})
export class AdvertisementComponent {
  private readonly advertisementService = inject(AdvertisementService)

  /** Page type this banner sits on. */
  readonly placement = input.required<AdvertisementPlacement>()
  /** Numeric Payload id of the magazine or article, where applicable. */
  readonly referenceId = input<number | null>()

  /**
   * Refetched whenever the slot changes, so navigating between two articles
   * re-rolls the banner instead of keeping the previous article's one.
   */
  protected readonly advertisement = toSignal(
    toObservable(
      computed(() => ({
        placement: this.placement(),
        referenceId: this.referenceId() ?? null,
      }))
    ).pipe(
      takeUntilDestroyed(),
      switchMap(({ placement, referenceId }) =>
        // Cleared while the next slot loads: without this the previous
        // article's banner stays up on the new page, and stays there for good
        // if that request never lands.
        this.advertisementService
          .getRandom(placement, referenceId)
          .pipe(startWith(null))
      )
    )
  )

  /**
   * The advertiser's own description of the artwork, if they gave one - the CMS
   * leaves an untouched field as an empty string rather than as null.
   */
  protected readonly artworkAlt = computed(() =>
    firstText(this.advertisement()?.alt)
  )

  /** Band behind the desktop artwork, used from the `md` breakpoint up. */
  protected readonly desktopBand = computed(() =>
    bandFor(this.advertisement()?.imageDesktop, DESIGN.desktop)
  )

  /** Band behind the mobile artwork, used below the `md` breakpoint. */
  protected readonly mobileBand = computed(() =>
    bandFor(this.advertisement()?.imageMobile, DESIGN.mobile)
  )

  /**
   * Set when the artwork fails to load, so the browser's broken-image
   * placeholder makes way for the plain grey band. Reset for every new banner,
   * since the next one may well load fine.
   */
  protected readonly artworkFailed = linkedSignal({
    source: this.advertisement,
    computation: () => false,
  })

  /**
   * Whether the slot has to hold its designed shape itself, because the API did
   * not say how large the artwork is.
   *
   * Normally the artwork's own dimensions give the slot its height, which also
   * covers an upload that fails to load: the browser keeps the box the
   * `width`/`height` attributes describe. Without them a missing or broken
   * image would collapse the slot to nothing and take the grey band with it, so
   * the design canvas stands in.
   */
  protected readonly usesDesignBox = computed(() => {
    const advertisement = this.advertisement()
    if (!advertisement) return false

    return (
      !hasSize(advertisement.imageDesktop) ||
      !hasSize(advertisement.imageMobile)
    )
  })
}
